import { ReplaySubject } from 'rxjs';

import { Logger } from '../../models/logger';
import { CanvasManager } from './canvas-manager';
import { FloatingGraphic, FloatingSVGGraphic } from './clip-art';
import { Graphic } from './graphic';
import { MemeStoredImage } from './meme-image';
import { MemeText } from './meme-text';
import { Dimension, Location, Rectangle } from './meme.types';



/**
 * Compose of MemeText and MemeImage
 */
export class Meme {
    private readonly logger = Logger.getLogger('Meme');

    canvasManager: CanvasManager;
    images: MemeStoredImage[] = [];
    graphics: Graphic[] = [];
    memeWidth = 700;

    selectedItem: any;
    selectedItemLocation: Location;

    onMemeSizeChange = new ReplaySubject<void>();

    constructor(memeWidth: number) {
        this.memeWidth = memeWidth;
        const c = document.getElementById('canvas') as any;
        this.canvasManager = new CanvasManager(c, memeWidth, 500);

        this.onMemeSizeChange.subscribe(() => {
            const memeDim = this.getMemeDimension();
            this.canvasManager.updateCanvasDimension(memeDim.width, memeDim.height);
            this.paint();
        });
    }

    getCanvasManager() {
        return this.canvasManager;
    }

    public getImages() {
        return this.images;
    }

    async changeImageSrc(image: MemeStoredImage, newSrc: string) {
        await image.setImage(newSrc);
        this.images.forEach(i => i.updateBoundingRectangle());
        this.onMemeSizeChange.next();
    }

    async addImage(image: MemeStoredImage) {
        await image.loadImage();
        this.images.push(image);
        this.images.forEach(i => i.updateBoundingRectangle());
        this.onMemeSizeChange.next();
    }

    async removeImage(image: MemeStoredImage) {
        this.images = this.images.filter(f => f !== image);
        this.images.forEach(i => i.updateBoundingRectangle());
        this.onMemeSizeChange.next();
    }

    async addImageBelow(existingImage: MemeStoredImage, newImage: MemeStoredImage) {
        await newImage.loadImage();
        const index = this.images.indexOf(existingImage);
        this.images.splice(index + 1, 0, newImage);
        this.images.forEach(i => i.updateBoundingRectangle());
        this.onMemeSizeChange.next();
    }

    async addImageAbove(existingImage: MemeStoredImage, newImage: MemeStoredImage) {
        await newImage.loadImage();
        const index = this.images.indexOf(existingImage);
        this.images.splice(index, 0, newImage);
        this.images.forEach(i => i.updateBoundingRectangle());
        this.onMemeSizeChange.next();
    }

    swapWithAbove(memeImage: MemeStoredImage) {
        const index = this.images.indexOf(memeImage);
        const temp = this.images[index];
        this.images[index] = this.images[index - 1];
        this.images[index - 1] = temp;
        this.images.forEach(i => i.updateBoundingRectangle());
        this.onMemeSizeChange.next();
    }

    swapWithBelow(memeImage: MemeStoredImage) {
        const index = this.images.indexOf(memeImage);
        const temp = this.images[index];
        this.images[index] = this.images[index + 1];
        this.images[index + 1] = temp;
        this.images.forEach(i => i.updateBoundingRectangle());
        this.onMemeSizeChange.next();
    }

    deleteImage(memeImage: MemeStoredImage) {
        if (this.images.length <= 1) {
            throw new Error('Can not delete last image');
        }
        this.images = this.images.filter(i => i !== memeImage);
        this.images.forEach(i => i.updateBoundingRectangle());
        this.onMemeSizeChange.next();
    }

    addText(text: MemeText) {
        this.graphics.push(text);
    }

    addGraphic(graphic: FloatingGraphic) {
        this.graphics.push(graphic);
    }

    deleteText(text: MemeText) {
        const index = this.graphics.indexOf(text);
        this.graphics.splice(index, 1);
    }

    deleteGraphic(graphic: Graphic) {
        const index = this.graphics.indexOf(graphic);
        this.graphics.splice(index, 1);
    }

    setSelectedItem(item: any, location: Location) {
        this.selectedItem = item;
        this.selectedItemLocation = location;
    }

    resizeGraphicContainer(item: any, location: Location, scale: number) {
        item.resize(location, scale);
    }

    moveGraphicContainer(item: any, mouseLocation: Location, scale: number) {
        const canvasBounds: Rectangle = this.canvasManager.getContainerBoundingRect();
        const newLocation = {
            x: ((mouseLocation.x - canvasBounds.x) / scale) + this.selectedItemLocation.x,
            y: ((mouseLocation.y - canvasBounds.y) / scale) + this.selectedItemLocation.y,
        };
        item.move(newLocation);
    }

    rotateGraphicContainer(item: any, mouseLocation: Location, scale: number) {
        // Rotation logic typically depends on angles which are invariant to uniform scaling.
        // However, if needed, we can normalize coordinates here. 
        // For now, assuming standard rotation logic exists or will exist:
        const canvasBounds: Rectangle = this.canvasManager.getContainerBoundingRect();
        /*
        const center: Location = {
            x: canvasBounds.x + (canvasBounds.width / 2),
            y: canvasBounds.y + (canvasBounds.height / 2),
        };
        */
        const newLocation = {
            x: (mouseLocation.x - canvasBounds.x) + this.selectedItemLocation.x, // Viewport coords
            y: (mouseLocation.y - canvasBounds.y) + this.selectedItemLocation.y,
        };
        item.rotate(newLocation);
    }

    repositionOutOfViewGraphics() {
        try {
            const graphics: Graphic[] = this.getGraphics();
            for (const g of graphics) {
                const r = g.getBoundingRect();
                if ((r.y + r.height < 0 || r.x + r.width < 0) ||
                    (r.y > this.getMemeDimension().height || r.x > this.getMemeDimension().width)
                ) {
                    g.moveToView(this.getMemeDimension());
                }
            }
        } catch (e) {
            this.logger.error({
                message: 'Error repositioning out of view graphics.',
                error: e,
            });
        }
    }

    hideAllTextEditPanels() {
        if (this.graphics) {
            this.graphics.forEach(graphic => {
                if (graphic instanceof MemeText) {
                    graphic.hideEditPanel();
                }
            });
        }
    }

    public paint() {
        this.updateTextSize();
        this.updateImageBoundingRectangle();
        const memeDim = this.getMemeDimension();
        this.canvasManager.updateCanvasDimension(memeDim.width, memeDim.height);
        this.repositionOutOfViewGraphics();
        this.paintImages();
        this.paintGraphicsAndText();
    }

    private updateTextSize() {
        if (this.graphics) {
            this.graphics.forEach(graphic => {
                if (graphic instanceof MemeText) {
                    graphic.setFontSize(this.canvasManager.getFontSizeToFitContainerWidth(graphic));
                }
            });
        }
    }

    private updateImageBoundingRectangle() {
        if (this.images) {
            this.images.forEach(image => {
                image.updateBoundingRectangle();
            });
        }
    }

    // All images must be loaded and bounding rectangle should be calculated before calling this
    private paintImages() {
        if (this.images) {
            for (const image of this.images) {
                const imageContainerRect = image.getBoundingRectangle();
                this.canvasManager.drawRect(imageContainerRect, image.getBackgroundColor());
                const imageRect = image.getImageBoundingRectangle();
                this.canvasManager.drawImage(image.getImage(), imageRect.x, imageRect.y, imageRect.width, imageRect.height);
            }
        }
    }

    private paintGraphicsAndText() {
        const graphics = [];
        if (this.graphics && this.graphics.length > 0) {
            graphics.push(...this.graphics);
        }
        graphics.sort((a, b) => a.getLocation().z - b.getLocation().z);
        graphics.forEach(graphic => {
            if (graphic instanceof MemeText) {
                this.canvasManager.updateTextBoundingRectangle(graphic);
                this.canvasManager.drawText(graphic);
            } else if (graphic instanceof FloatingSVGGraphic) {
                this.canvasManager.updateGraphicBoundingRectangle(graphic);
                this.canvasManager.drawImage(graphic.getGraphic(), graphic.getBoundingRect());
            }

        });
    }

    public getMemeWidth() {
        return this.canvasManager.getCanvasWidth();
    }

    public getSortedGraphics(type: string) {
        if (!type) {
            return this.graphics.sort((a, b) => a.getLocation().z = b.getLocation().z);
        }
        return this.graphics.filter(a => a.getType() === type).sort((a, b) => a.getLocation().z = b.getLocation().z);
    }

    public getMemeDimension(): Dimension {
        let height = 0;
        this.images.forEach(image => {
            height += image.getBoundingRectangle().height;
        });
        const width = this.getMemeWidth();
        return {
            width: width,
            height: height,
        };
    }

    private getGraphics() {
        return this.graphics;
    }

    private doOverlap(rect1: Rectangle, rect2: Rectangle) {

        const l1: Location = {
            x: rect1.x,
            y: rect1.y,
        };
        const r1: Location = {
            x: rect1.x + rect1.width,
            y: rect1.y + rect1.height,
        };
        const l2: Location = {
            x: rect2.x,
            y: rect2.y,
        };
        const r2: Location = {
            x: rect2.x + rect2.width,
            y: rect2.y + rect2.height,
        };
        // If one rectangle is on left side of other
        if (l1.x > r2.x || l2.x > r1.x) {
            return false;
        }

        // If one rectangle is above other
        if (l1.y < r2.y || l2.y < r1.y) {
            return false;
        }

        return true;
    }
}

